import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Container } from "../components/zen/Container";
import { Eyebrow } from "../components/zen/Eyebrow";
import { Heading } from "../components/zen/Heading";
import { LogoGlyph } from "../components/zen/Logo";
import { setAdminToken } from "./adminFetch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
    const navigate = useNavigate();
    const [userId, setUserId] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        if (!userId.trim() || !password) {
            setError("Please enter your credentials.");
            return;
        }
        setSubmitting(true);
        try {
            const resp = await fetch(`${API}/admin/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ userId: userId.trim(), password }),
            });
            if (resp.status === 401) {
                setError("Invalid credentials.");
                return;
            }
            if (resp.status === 429) {
                setError("Too many attempts. Try again in a few minutes.");
                return;
            }
            if (!resp.ok) {
                setError("Network error. Please try again.");
                return;
            }
            const data = await resp.json();
            setAdminToken(data.token, data.expiresAt);
            navigate("/admin/leads");
        } catch (err) {
            setError("Network error. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="relative min-h-screen bg-base text-text-primary"
            data-testid="admin-login-page"
        >
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <motion.div
                    aria-hidden
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.6 }}
                    className="absolute left-1/2 top-1/2 h-[520px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full"
                    style={{
                        background:
                            "radial-gradient(ellipse at center, rgba(124,255,79,0.08) 0%, transparent 60%)",
                        filter: "blur(80px)",
                    }}
                />
                <div className="zen-grid opacity-50" />
            </div>

            <header className="relative z-10 px-5 py-5">
                <Container>
                    <div className="inline-flex items-center gap-2.5 text-text-primary">
                        <LogoGlyph size={20} />
                        <span
                            className="font-display text-[13.5px] font-semibold tracking-tight-2"
                            style={{ letterSpacing: "0.05em" }}
                        >
                            ZENME&nbsp;GROUP · ADMIN
                        </span>
                    </div>
                </Container>
            </header>

            <main className="relative z-10 flex min-h-[calc(100vh-80px)] items-center justify-center px-5 py-10">
                <div className="w-full max-w-md">
                    <div className="text-center">
                        <Eyebrow withDot>Admin Console</Eyebrow>
                        <Heading as="h1" fluidSize="h3" className="mt-5">
                            Admin Sign In
                        </Heading>
                        <p className="mt-3 text-[14.5px] text-text-secondary">
                            Internal access only.
                        </p>
                    </div>
                    <form
                        onSubmit={onSubmit}
                        noValidate
                        data-testid="admin-login-form"
                        className="mt-8 rounded-2xl border border-border-subtle bg-elevated p-8 space-y-5"
                    >
                        <div>
                            <label className="block text-[12px] font-medium uppercase tracking-widest-2 text-text-secondary">
                                User ID
                            </label>
                            <input
                                type="text"
                                value={userId}
                                onChange={(e) => setUserId(e.target.value)}
                                autoComplete="username"
                                data-testid="admin-userid"
                                className="mt-2 w-full rounded-lg border border-border-subtle bg-[var(--bg-elevated-2)] px-4 py-3.5 text-[15px] text-text-primary placeholder:text-text-muted focus:outline-none focus:border-[rgba(124,255,79,0.55)] focus:ring-[3px] focus:ring-[rgba(124,255,79,0.15)]"
                            />
                        </div>
                        <div>
                            <label className="block text-[12px] font-medium uppercase tracking-widest-2 text-text-secondary">
                                Password
                            </label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="current-password"
                                data-testid="admin-password"
                                className="mt-2 w-full rounded-lg border border-border-subtle bg-[var(--bg-elevated-2)] px-4 py-3.5 text-[15px] text-text-primary placeholder:text-text-muted focus:outline-none focus:border-[rgba(124,255,79,0.55)] focus:ring-[3px] focus:ring-[rgba(124,255,79,0.15)]"
                            />
                        </div>
                        {error && (
                            <p
                                role="alert"
                                data-testid="admin-login-error"
                                className="text-[13px] text-red-400/90"
                            >
                                {error}
                            </p>
                        )}
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="admin-login-submit"
                            className="zen-glow-on-hover inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--accent)] px-6 py-3.5 text-[14.5px] font-medium text-[#0a0a0b] transition-all duration-300 hover:brightness-110 disabled:opacity-60 disabled:pointer-events-none"
                        >
                            {submitting && (
                                <Loader2
                                    className="h-4 w-4 animate-spin"
                                    strokeWidth={2}
                                />
                            )}
                            <span>{submitting ? "Signing in..." : "Sign In"}</span>
                        </button>
                    </form>
                </div>
            </main>
        </div>
    );
}
